'use client';

import { useEffect, useState, type ComponentType } from 'react';
import { usePathname } from 'next/navigation';
import AttributionCapture from '@/components/marketing/AttributionCapture';
import { isAiChatLauncherPath } from '@/lib/ai/chat-launcher-paths';
import {
  isFooterLandingPath,
  isMarketingChromePath,
} from '@/lib/public-site-paths';

type AnyComponent = ComponentType;

/**
 * Public-page pieces. Each import() runs only when this page shows that piece,
 * so dashboard and sign-in never download them. AttributionCapture is the
 * exception: it is tiny and has to run on whichever page a visitor lands on.
 */
export function PublicSiteChrome() {
  const pathname = usePathname();
  const showMarketingChrome = isMarketingChromePath(pathname);
  const showFooter = isFooterLandingPath(pathname);
  const showChat = isAiChatLauncherPath(pathname);

  const [CookieConsent, setCookieConsent] = useState<AnyComponent | null>(null);
  const [ScrollToTop, setScrollToTop] = useState<AnyComponent | null>(null);
  const [FooterLanding, setFooterLanding] = useState<AnyComponent | null>(null);
  const [AIChatWidget, setAIChatWidget] = useState<AnyComponent | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (showMarketingChrome) {
      void import('@/components/cookie-consent').then(mod => {
        if (!cancelled) setCookieConsent(() => mod.CookieConsent);
      });
      void import('@/components/scroll-to-top').then(mod => {
        if (!cancelled) setScrollToTop(() => mod.ScrollToTop);
      });
    } else {
      setCookieConsent(null);
      setScrollToTop(null);
    }

    if (showFooter) {
      void import('@/components/footer-landing').then(mod => {
        if (!cancelled) setFooterLanding(() => mod.FooterLanding);
      });
    } else {
      setFooterLanding(null);
    }

    if (showChat) {
      void import('@/components/AIChatWidget').then(mod => {
        if (!cancelled) setAIChatWidget(() => mod.default);
      });
    } else {
      setAIChatWidget(null);
    }

    return () => {
      cancelled = true;
    };
  }, [showMarketingChrome, showFooter, showChat]);

  return (
    <>
      <AttributionCapture />
      {CookieConsent ? <CookieConsent /> : null}
      {ScrollToTop ? <ScrollToTop /> : null}
      {FooterLanding ? <FooterLanding /> : null}
      {AIChatWidget ? <AIChatWidget /> : null}
    </>
  );
}
